// The undo stack: one snapshot per turn taken, and the rules about when it is
// not allowed to hold one.
//
// **Entirely on the client's side of the boundary, deliberately.** The
// simulation knows about a turn clock (`physics.clock.msPerTurn`) because that
// changes what the world does; it does not and must not know that anyone is
// keeping the last sixty-four of them in a list. A world does not have a
// history; a player does.
//
// **Only in turn mode.** Undo over the wall clock is coherent to implement and
// misleading to use: the snapshot restores where the player was, but not where
// the world was going, so pressing it hands back a position the room has already
// decayed past. The two ship together or not at all (see settings.ts).
//
// **Snapshots, not replay from the start.** Both work -- the simulation is fully
// deterministic (no `Math.random`, no clock read anywhere in `sim/`; see
// `sim/mob.ts` on why the one thing that wanted randomness is a hash instead),
// which is what makes `replay.ts` possible at all and would make a
// rewind-and-reapply undo correct too. Snapshots win on the property that
// matters at the moment of use: undo is O(1) and cannot get slower the longer
// the player has been in the room, which is exactly when they need it most.
import { WorldSnapshot, WorldState, restoreWorldState, snapshotWorld } from '../../rune-lab/src/sim/state';
import { saveLearnedRunes } from './learnedRunes';

/** How many turns back the player can go.
 *
 * Deep enough that "undo my way out of this" is a real option rather than a
 * single mercy, shallow enough to bound the memory: a snapshot is one room, and
 * the largest shipped room is ~25 KB of JSON, so the worst case here is a couple
 * of megabytes on a phone. Not a tuning dial in `PhysicsConfig` -- it changes
 * nothing about how the world behaves, only how much of its past this client
 * happens to be holding. */
const HISTORY_LIMIT = 64;

/** The room the stack currently describes. Every entry in `history` is a
 * snapshot of *this* room, because a snapshot only covers one (see
 * `WorldSnapshot`), so the two move together or the stack is cleared. */
let historyRoomId: string | null = null;
let history: WorldSnapshot[] = [];
const availabilityListeners: Array<(canUndo: boolean) => void> = [];

export function canUndo(): boolean {
  return history.length > 0;
}

export function undoDepth(): number {
  return history.length;
}

/** Called by the HUD so the Undo button can disable itself rather than sitting
 * there looking pressable at the start of a room. */
export function onUndoAvailabilityChange(listener: (canUndo: boolean) => void): void {
  availabilityListeners.push(listener);
}

export function refreshUndoAvailability(): void {
  const available = canUndo();
  availabilityListeners.forEach((listener) => listener(available));
}

/** Throws the history away.
 *
 * Called wherever the world moves in a way a snapshot cannot describe or should
 * not survive: crossing a door (a snapshot covers one room), New Game, loading a
 * save, and restarting a room after death. Each of those is a deliberate break
 * in continuity, and being able to undo *across* one would mean undoing back into
 * a room the player chose to leave -- or, worse, back across a death, which
 * would make dying free. */
export function clearUndoHistory(): void {
  historyRoomId = null;
  history = [];
  refreshUndoAvailability();
}

/** Takes the snapshot for the turn that is about to happen.
 *
 * Called *before* the action, not after, because what undo wants back is the
 * world as it was when the player still had the choice.
 *
 * Self-healing on a room change rather than trusting every caller to have
 * cleared: if the world is in a different room than the stack describes, the
 * stack is dropped and this snapshot starts a new one. Door crossings do call
 * `clearUndoHistory` explicitly, and this covers everything else that moves the
 * caster between rooms -- the Developer Tools room jump, a playtest injection --
 * without each of them having to remember. */
export function recordUndoPoint(world: WorldState): void {
  if (historyRoomId !== world.player.roomId) {
    historyRoomId = world.player.roomId;
    history = [];
  }
  history.push(snapshotWorld(world));
  if (history.length > HISTORY_LIMIT) history.shift();
  refreshUndoAvailability();
}

/** Drops the most recent snapshot, for an action that turned out not to be one.
 *
 * A snapshot has to be taken *before* an action, which means taking it before
 * knowing whether the action will happen: walking into a wall goes through the
 * same path as walking into open floor and only the sim can tell them apart.
 * Without this, bumping a wall would leave an entry on the stack and light up the
 * Undo button for a move that never occurred -- and pressing it would restore a
 * world identical to the current one, which reads as the button being broken.
 *
 * Deliberately not a general "pop": it is only ever correct immediately after the
 * `recordUndoPoint` it cancels, before anything else touches the stack. */
export function discardUndoPoint(): void {
  history.pop();
  refreshUndoAvailability();
}

/** Steps one turn back, or reports that there was nothing to step back to.
 *
 * Returns true when the world moved, which the caller must treat as an
 * instruction to rebuild the room's visuals **wholesale** rather than to diff
 * them. That is not a suggestion about tidiness: `restoreWorldState` rewinds the
 * runtime-object id counters, so a sprite left over from before the restore
 * could be pointing at an id that now belongs to a different object. Total
 * teardown is what makes rewinding those counters safe (see the doc comment on
 * `restoreWorldState`). */
export function undo(world: WorldState): boolean {
  const snapshot = history.pop();
  if (!snapshot) return false;
  restoreWorldState(world, snapshot);
  // The one piece of restored state that is mirrored outside the world.
  // `learnedRunes` lives in `WorldState`, but `learnedRunes.ts` also writes it
  // to localStorage as it changes -- so undoing the cast that read a rune book
  // would put the rune back out of the world's set and leave it in the browser's,
  // and the next reload would hand it back. Undo has to be true across a reload
  // or it is not undo. Everything else the client persists is deliberately *not*
  // rewound: a tutorial hint stays seen, a notebook entry stays written, and an
  // analytics event that has already been sent cannot be recalled.
  saveLearnedRunes();
  refreshUndoAvailability();
  return true;
}
